import { useState, useCallback } from "react";
import { useSaveStoryboard } from "../../storyboard/hooks/useSaveStoryboard";
import { useTemplateData } from "./useTemplateData";
import {
  Storyboard,
  StoryboardSection,
  TDetailedStoryboard,
} from "pages/media/types/";

interface UseStoryboardEditingParams {
  projectId: string;
  organizationId: string;
  storyboardId: string | undefined;
  primaryDocumentId: string | undefined;
  // analytics
  trackUpdate?: () => void;
  // BE deliberately skips the media mark on a Storyboard asset patch (it does
  // it for Slides only) — highlight edits patch the storyboard too, and those
  // don't change the rendered video. Every edit routed through here is real
  // script content, so flag the media ourselves. Omit it when the caller has
  // already patched the Slides asset. It also refreshes the project cache,
  // so a caller navigating away right after an edit doesn't leave the
  // out-of-date widget showing a stale state (see MED-6969).
  markMediaOutOfDate?: () => Promise<void>;
}

const getSectionById = (
  sections: StoryboardSection[],
  sectionId: string
): StoryboardSection | undefined => {
  return sections.find((s) => s.id === sectionId);
};

export const useStoryboardEditing = ({
  projectId,
  organizationId,
  storyboardId,
  primaryDocumentId,
  trackUpdate,
  markMediaOutOfDate,
}: UseStoryboardEditingParams) => {
  const {
    storyboardSections: sections,
    updateSection,
    storyboard: storyboardData,
  } = useTemplateData();

  const { saveStoryboard, isStoryboardSaving } = useSaveStoryboard({
    projectId,
    organizationId,
    storyboardId,
    documentId: primaryDocumentId,
  });

  // Track which section is currently being saved
  const [savingSectionId, setSavingSectionId] = useState<string | null>(null);

  const saveStoryboardAsset = useCallback(
    async (
      sectionId: string,
      updatedStoryboard: Storyboard | TDetailedStoryboard
    ) => {
      setSavingSectionId(sectionId);
      let saved = false;
      try {
        saved = await saveStoryboard(updatedStoryboard);
      } finally {
        setSavingSectionId(null);
      }
      // `saveStoryboard` reports a failed write by resolving to false rather
      // than rejecting, so gate on it: marking the media stale for content
      // that never persisted would prompt a pointless regeneration.
      if (!saved) return;

      // Best-effort: a failed mark shouldn't surface as a save error, the save
      // itself already succeeded above.
      await markMediaOutOfDate?.().catch((error) => {
        console.error("Error marking the media out of date", error);
      });

      trackUpdate?.();
    },
    [saveStoryboard, trackUpdate, markMediaOutOfDate]
  );

  const updateStoryboardSection = useCallback(
    async (sectionId: string, updates: Partial<StoryboardSection>) => {
      const oldSection = getSectionById(sections, sectionId);
      if (!oldSection) return;

      updateSection(sectionId, updates);

      const updatedStoryboard = storyboardData
        ? {
            ...storyboardData,
            sections: storyboardData.sections.map((section) =>
              section.id === sectionId ? { ...section, ...updates } : section
            ),
          }
        : undefined;

      await saveStoryboardAsset(sectionId, updatedStoryboard);
    },
    [sections, updateSection, saveStoryboardAsset, storyboardData]
  );

  // Helper to check if a specific section is being saved
  const isSectionSaving = (sectionId: string) => {
    return isStoryboardSaving && savingSectionId === sectionId;
  };

  return {
    updateStoryboardSection,
    isStoryboardSaving,
    savingSectionId,
    isSectionSaving,
  };
};
